/**
 * Settings Dialog — theme selector + What's New changelog
 */

import { useState } from "react";
import { useSnapshot } from "valtio";
import { Sun, Moon, Monitor, Sparkles, Search } from "lucide-react";
import { store, actions, type Theme } from "@/lib/store";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";

const THEME_OPTIONS: { id: Theme; label: string; icon: React.ReactNode }[] = [
  { id: "light", label: "Light", icon: <Sun className="h-4 w-4" /> },
  { id: "dark", label: "Dark", icon: <Moon className="h-4 w-4" /> },
  { id: "system", label: "System", icon: <Monitor className="h-4 w-4" /> },
];

export function SettingsDialog() {
  const { settingsOpen, settings } = useSnapshot(store);
  const [changeFilter, setChangeFilter] = useState("");

  if (!settingsOpen) return null;

  const q = changeFilter.toLowerCase();
  const filteredChanges = [...__RECENT_CHANGES__]
    .filter(
      (c) =>
        !q ||
        c.message.toLowerCase().includes(q) ||
        c.linearIds.some((id) => id.toLowerCase().includes(q))
    )
    .sort((a, b) => {
      const order = { fix: 0, feat: 1, perf: 2 };
      return order[a.type] - order[b.type];
    });

  return (
    <Dialog
      open={settingsOpen}
      onClose={() => actions.setSettingsOpen(false)}
      size="lg"
    >
      <DialogContent className="max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader onClose={() => actions.setSettingsOpen(false)}>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-6 overflow-y-auto max-h-[calc(85vh-5rem)]">
            {/* Theme Selector */}
            <div>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Theme
              </label>
              <div className="flex gap-2">
                {THEME_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => actions.setTheme(option.id)}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition-colors",
                      settings.theme === option.id
                        ? "border-vizimix-500 bg-vizimix-500/20 text-vizimix-100"
                        : "border-organize-border text-vizimix-400 hover:border-vizimix-500/50 hover:text-vizimix-200"
                    )}
                  >
                    {option.icon}
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* What's New */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-vizimix-400" />
                <span className="text-sm font-medium text-vizimix-200">
                  What&apos;s New
                </span>
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-vizimix-500/20 text-vizimix-400">
                  v{__APP_VERSION__}
                </span>
              </div>

              {__RECENT_CHANGES__.length > 0 ? (
                <div className="space-y-2">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-vizimix-500" />
                    <input
                      type="text"
                      value={changeFilter}
                      onChange={(e) => setChangeFilter(e.target.value)}
                      placeholder="Search changes or VIZ-xxx..."
                      className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-organize-bg border border-organize-border text-xs text-vizimix-100 placeholder:text-vizimix-500 focus:outline-none focus:ring-2 focus:ring-vizimix-500 focus:ring-offset-2 focus:ring-offset-organize-card"
                    />
                  </div>

                  {/* Changes list */}
                  <div className="max-h-80 overflow-y-auto space-y-1.5">
                    {filteredChanges.length > 0 ? (
                      filteredChanges.map((change) => (
                        <div
                          key={change.hash}
                          className="flex items-start gap-2 text-sm"
                        >
                          <span
                            className={cn(
                              "text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 mt-0.5",
                              change.type === "fix" &&
                                "bg-green-500/20 text-green-400",
                              change.type === "feat" &&
                                "bg-blue-500/20 text-blue-400",
                              change.type === "perf" &&
                                "bg-orange-500/20 text-orange-400"
                            )}
                          >
                            {change.type === "feat"
                              ? "New"
                              : change.type === "fix"
                                ? "Fix"
                                : "Perf"}
                          </span>
                          <span className="text-vizimix-200 flex-1 min-w-0 break-words leading-tight">
                            {change.message}
                            {change.scope && (
                              <span className="text-[10px] text-vizimix-500 ml-1.5">
                                {change.scope}
                              </span>
                            )}
                            {change.linearIds.map((id) => (
                              <span
                                key={id}
                                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 ml-1.5"
                              >
                                {id}
                              </span>
                            ))}
                          </span>
                          <span className="text-[10px] text-vizimix-500 shrink-0 mt-0.5">
                            {change.date}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-vizimix-500 text-center py-2">
                        No matching changes
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-vizimix-500">No recent changes</p>
              )}

              <p className="text-[10px] text-vizimix-500 mt-2">
                Built {new Date(__BUILD_TIME__).toLocaleString()}
              </p>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
